import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { IoCloseCircleOutline } from "react-icons/io5";
import orgApi from "../../../api/org_api";
import {
  Breadcrumb,
  Button,
  CustomDatePicker,
  FooterButtons,
  FunnelIconButton,
  IconButton,
  Label,
  Paragraph,
  Table,
  Text,
} from "../../../component/common";
import { SearchableDropdown } from "../../../component/common/SearchableDropdown";
import PaginationButtons from "../../../component/PaginationButtons";
import S from "../../../Services/GeneralServices";
import { sevenDaysAgo } from "../../../utils/constant";

type InterviewReportProps = { setLoading: Function };

const InterviewReport = ({ setLoading }: InterviewReportProps) => {
  const [schedule, setSchedule] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPost, setTotal] = useState(0);
  const [verdict, setVerdict] = useState("");
  const [csvData, setCsvData] = useState([] as any);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [dateFilter, setDateFilter] = useState<{
    from: Date | null;
    to: Date | null;
  }>({ from: sevenDaysAgo, to: new Date() });

  useEffect(() => {
    fetchData();
  }, [currentPage, verdict]);

  const fetchData = async () => {
    const page = totalPages === 0 ? 1 : Math.min(currentPage + 1, totalPages);
    const org = JSON.parse(localStorage.getItem("user__details")?.toString()!);
    const reqData = {
      page: page,
      ...getBodyDate(dateFilter.from, dateFilter.to),
      recommended_status: verdict === "" ? null : verdict,
    };
    const res = await orgApi.interviewReport(org.id, reqData);
    if (res.data.success) {
      setTotal(res.data.total);
      var t = Math.ceil(res.data.total / 10);
      setTotalPages(t);
      setSchedule(res.data.schedule);
    } else {
      setSchedule([]);
    }
  };

  const getBodyDate = (
    start: Date | null,
    end: Date | null,
  ): {
    from: string;
    to: string;
  } => {
    const from = start
      ? format(start, "yyyy-MM-dd 00:00:00")
      : !start && end
        ? format(new Date(), "yyyy-MM-dd 00:00:00")
        : "";

    const to = end
      ? format(end, "yyyy-MM-dd 23:59:59")
      : !end && start
        ? format(new Date(), "yyyy-MM-dd 23:59:59")
        : "";
    return { from, to };
  };

  const exportData = async () => {
    setLoading(true);
    const org = JSON.parse(localStorage.getItem("user__details")?.toString()!);
    const reqData = getBodyDate(dateFilter.from, dateFilter.to);
    const res = await orgApi.exportInterviewReport(org.id, reqData);

    if (res.data.success) {
      const datas = res.data.schedule;
      const data = [];
      for (let index = 0; index < datas.length; index++) {
        const element = datas[index];
        const val = {
          PostTitle: element.post.job_title,
          RecruiterName: element.org.first_name + "" + element.org.last_name,
          ClientName: element.post.client_name,
          Status: `${element.status === "NR" ? "Next Round" : element.status}`,
          CandidateName:
            element.candidate.first_name + " " + element.candidate.last_name,
          InterviewerName: `${
            element.interviewer
              ? element.interviewer.candidate.first_name +
                " " +
                element.interviewer.candidate.last_name
              : "-"
          }`,

          InterviewedDate: format(element.utc_starttime, "dd MMM yyyy"),
          InterviewedTime: element.utc_endtime
            ? format(element.utc_starttime, "hh:mm a") +
              " " +
              format(element.utc_endtime, "hh:mm a")
            : "-",
          Verdict:
            element.feedback.length > 0
              ? element.feedback[0].recommended_status
                ? element.feedback[0].recommended_status
                : "NA"
              : "NA",
          CandidateNoShow:
            element.feedback.length > 0
              ? element.feedback[0].candidate_no_show
                ? "Yes"
                : "No"
              : "-",
          CandidateProxy:
            element.feedback.length > 0
              ? element.feedback[0].proxy
                ? "Yes"
                : "No"
              : "-",
        } as any;
        if (S.getOrgDomain() !== "nam-it.com") {
          val["SITScore"] = `${
            element.feedback.length > 0
              ? element.feedback[0].sit_score
              : element.sit
          }`;
        } else {
          val["RecommendedStatus"] = `${
            element.feedback.length > 0
              ? element.feedback[0].eligible_for_this_position
                ? "Yes"
                : "No"
              : "Yes"
          }`;
        }
        data.push(val);
      }
      setCsvData(data);
      setTimeout(() => {
        document.getElementById("expbtn")?.click();
      }, 1000);
    }
  };

  const handleFromDateChange = (date: any) =>
    setDateFilter({ ...dateFilter, from: date });
  const handleToDateChange = (date: any) =>
    setDateFilter({ ...dateFilter, to: date });

  const getTableData = () => {
    const coloumns = [
      // { label: "S.No", accessor: "s_no" },
      { label: "Post Title", accessor: "post_title" },
      { label: "Recruiter Name", accessor: "recruiter_name" },
      { label: "Client Name", accessor: "client_name" },
      { label: "Status", accessor: "status" },
      { label: "Interviewer Name", accessor: "interviewer_name" },
      { label: "Interview Date", accessor: "interview_date" },
      { label: "Interview Time", accessor: "interview_time" },
      { label: "Candidate Name", accessor: "candidate_name" },
      { label: "Candidate Email", accessor: "candidate_email" },
      {
        ...(S.getOrgDomain() !== "nam-it.com"
          ? { label: "SIT Score", accessor: "sit_score" }
          : { label: "Feedback", accessor: "feedback" }),
      },
      {
        ...(S.getOrgDomain() === "nam-it.com" && {
          label: "Recommended Status",
          accessor: "recommended_status",
        }),
      },
      { label: "Verdict", accessor: "verdict" },
      { label: "No Show", accessor: "no_show" },
      { label: "Proxy", accessor: "proxy" },
    ];
    const data =
      schedule &&
      schedule.length > 0 &&
      schedule.map((x: any, index: number) => ({
        s_no: <Text key={index}>{currentPage * 10 + index + 1}</Text>,
        post_title: <Text>{x.post.job_title}</Text>,
        candidate_name: (
          <Text className="max-w-40">
            {x.candidate.first_name + " " + x.candidate.last_name}
          </Text>
        ),
        candidate_email: <Text className="max-w-40">{x.candidate.email}</Text>,
        recruiter_name: <Text>{x.org.first_name + " " + x.org.last_name}</Text>,
        client_name: <Text>{x.post.client_name || "-"}</Text>,
        status: <Text>{x.status === "NR" ? "Next Round" : x.status}</Text>,
        interviewer_name: (
          <Text>
            {x.interviewer
              ? x.interviewer.candidate.first_name +
                " " +
                x.interviewer.candidate.last_name
              : "-"}
          </Text>
        ),
        interview_date: <Text>{format(x.utc_starttime, "dd MMM yyyy")}</Text>,
        interview_time: (
          <Text>
            {x.utc_endtime
              ? format(x.utc_starttime, "hh:mm a") +
                " " +
                format(x.utc_endtime, "hh:mm a")
              : "-"}
          </Text>
        ),
        ...(S.getOrgDomain() !== "nam-it.com"
          ? {
              sit_score: (
                <Text
                  className="cursor-pointer hover:text-blue-500 hover:font-semibold"
                  onClick={() => {
                    x.feedback.length > 0
                      ? S.viewFeedback(x.id)
                      : window.open(x.feedback_url);
                  }}
                >
                  {x.feedback.length > 0
                    ? x.feedback[0].sit_score > 0
                      ? x.feedback[0].sit_score
                      : "0"
                    : "0"}
                </Text>
              ),
            }
          : {
              feedback: (
                <Text
                  className="text-blue-500 underline cursor-pointer hover:font-semibold"
                  onClick={() => {
                    x.feedback.length > 0
                      ? S.viewFeedback(x.id)
                      : window.open(x.feedback_url);
                  }}
                >
                  Link
                </Text>
              ),
            }),

        ...(S.getOrgDomain() === "nam-it.com" && {
          recommended_status: (
            <Text>
              {x.feedback.length > 0
                ? x.feedback[0].eligible_for_this_position
                  ? "Yes"
                  : "No"
                : "Yes"}
            </Text>
          ),
        }),
        verdict: (
          <Text>
            {x.feedback.length > 0
              ? x.feedback[0].candidate_no_show || x.feedback[0].proxy
                ? "NA"
                : x.feedback[0].recommended_status
              : "NA"}
          </Text>
        ),
        no_show: (
          <Text>
            {x.feedback.length > 0
              ? x.feedback[0].candidate_no_show
                ? "Yes"
                : "No"
              : "-"}
          </Text>
        ),
        proxy: (
          <Text>
            {x.feedback.length > 0 ? (x.feedback[0].proxy ? "Yes" : "No") : "-"}
          </Text>
        ),
      }));
    return { coloumns, data };
  };

  const INTERVIEWREPORT_BREADCRUMBS = [
    { text: "Reports", href: "/report_list" },
    { text: `Interview Report ${`(${totalPost || 0})`}`, isActive: true },
  ];

  const clearFilter = () => {
    setDateFilter({ from: null, to: null });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start ">
        <Breadcrumb items={INTERVIEWREPORT_BREADCRUMBS} />
        <div className="flex flex-row items-center gap-2 mb-2">
          <AnimatePresence>
            {showFilter && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute right-6 top-[116px] z-20 w-60 rounded-lg border border-gray-200 bg-white p-3 shadow-lg 2xl:w-72 2xl:p-4"
              >
                <div className="space-y-2 2xl:space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <Paragraph text="Filter" color="text-gray-500" />
                    <IconButton
                      icon={
                        <IoCloseCircleOutline className="h-3.5 w-3.5 2xl:h-4 2xl:w-4" />
                      }
                      onClick={() => {
                        clearFilter();
                        setShowFilter(false);
                      }}
                      color="red"
                      className="-my-1 -mr-1 p-1"
                    />
                  </div>
                  <div className="space-y-2">
                    <CustomDatePicker
                      label="Start Date"
                      selected={dateFilter.from}
                      onBlur={() =>
                        dateFilter.from
                          ? handleFromDateChange(dateFilter.from)
                          : handleFromDateChange(new Date())
                      }
                      onChange={(date) => handleFromDateChange(date)}
                      showYearDropdown
                      showMonthDropdown
                      maxDate={dateFilter.to || new Date()}
                    />
                    <CustomDatePicker
                      label="End Date"
                      selected={dateFilter.to}
                      onBlur={() =>
                        dateFilter.to
                          ? handleToDateChange(dateFilter.to)
                          : handleToDateChange(new Date())
                      }
                      onChange={(date) => handleToDateChange(date)}
                      minDate={dateFilter.from || undefined}
                      showYearDropdown
                      showMonthDropdown
                      maxDate={new Date()}
                    />
                  </div>
                  <FooterButtons
                    primaryOnClick={() => {
                      setDateFilter({
                        from: dateFilter.from,
                        to: dateFilter.to,
                      });
                      fetchData();
                      setShowFilter(false);
                    }}
                    secondaryOnClick={() => {
                      setDateFilter({ from: sevenDaysAgo, to: new Date() });
                    }}
                    primaryText="Apply"
                    secondaryText="Reset"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex items-center justify-end gap-2 w-[50%]">
          {S.getOrgDomain() !== "nam-it.com" && (
            <div className="flex w-full items-center gap-2">
              <Label
                text="Select Verdict"
                className=" w-full text-end text-md font-bold"
              />
              <SearchableDropdown
                options={[
                  { label: "All", value: "" },
                  { label: "Rejected", value: "Rejected" },
                  { label: "Near Fit", value: "Near Fit" },
                  { label: "Hire", value: "Hire" },
                  { label: "Strong Hire", value: "Strong Hire" },
                ]}
                value={verdict}
                onChange={(e) => setVerdict(e.value)}
                placeholder="Select Verdict"
                // className="w-[\0px]"
              />
            </div>
          )}
          <CSVLink
            className="bg-primary rounded px-2.5 py-1.5 text-xs text-white hidden"
            type="button"
            filename="InterviewReport.csv"
            data={csvData}
            id="expbtn"
            onClick={() => {
              setLoading(false);
            }}
          >
            Export to CSV
          </CSVLink>
          <Button
            type="button"
            onClick={() => exportData()}
            text="Export to CSV"
            className=" text-[12px]"
          />
          <FunnelIconButton
            className="p-2.5"
            onClick={() => setShowFilter(!showFilter)}
            active={!!dateFilter.from || !!dateFilter.to}
          />
        </div>
      </div>
      <Table
        columns={getTableData().coloumns}
        data={getTableData().data}
        isStickyHeader
        isStacicRow
        containerHeight="h-[calc(100vh-197px)] 2xl:h-[calc(100vh-220px)]"
      />
      {totalPost > 10 && (
        <PaginationButtons
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </div>
  );
};
export default InterviewReport;
